import { ParamList } from '@/collections/params';
import { ParamModel } from '@/models/param';

export interface IPresetParam {
  code: string;
  value: any[];
}

/**
 * Build the `reportParams` list to apply when the user picks a Quick Preset on
 * the Buying Table.
 *
 * A Quick Preset only carries a couple of params (sales_trend, topn). The
 * Buying Table's reorder calculation, however, relies on the FULL set of
 * params defined by the `inventory_replenishment` measure (including_preorders,
 * out_of_stock_days_excluding, ignore-reorder-point toggles, etc.). The old
 * applyInventoryPreset rebuilt `reportParams` from the preset alone, silently
 * dropping every other configured param. This helper rebuilds the complete set:
 *
 *   value precedence:  preset override  >  user's current value  >  measure default
 *
 * so the preset's params take effect while every other param the user
 * configured is preserved (and any param they never set falls back to its
 * measure default rather than going missing).
 */
export function buildPresetReportParams(
  measureParams: ParamList,
  currentParams: ParamList,
  preset: IPresetParam[],
): ParamList {
  const presetByCode = new Map<string, any[]>();
  for (const p of preset) {
    presetByCode.set(p.code, p.value);
  }

  const currentByCode = new Map<string, any[]>();
  for (const p of currentParams.items) {
    currentByCode.set(p.code, p.value);
  }

  const result = new ParamList();
  for (const measureParam of measureParams.items) {
    let rawValue: any;
    if (presetByCode.has(measureParam.code)) {
      rawValue = presetByCode.get(measureParam.code);
    } else if (currentByCode.has(measureParam.code)) {
      rawValue = currentByCode.get(measureParam.code);
    } else {
      rawValue = measureParam.default;
    }

    // Always hand ParamModel a fresh array, even when the source held a scalar
    // (or nothing) — downstream code assumes `value` is an array, and copying
    // avoids sharing a reference with the measure definition or current params.
    const value = Array.isArray(rawValue)
      ? [...rawValue]
      : (rawValue !== undefined && rawValue !== null ? [rawValue] : []);

    result.add(new ParamModel({
      code: measureParam.code,
      name: measureParam.name,
      value,
      valueType: measureParam.valueType,
      dataType: measureParam.dataType,
      listValues: measureParam.listValues,
      sortOrder: measureParam.sortOrder,
      default: measureParam.default,
    }));
  }
  return result;
}
